import { useEffect, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { PART_STATUS_LABELS, REPAIR_PRIORITY_LABELS, REPAIR_STATUS_LABELS } from '@kmdx/contracts';
import { formatDateTime } from '@/lib/format';
import { useAddPart, useDeletePart, useRepair, useUpdatePart, useUpdateRepair } from '@/api/hooks/maintenance';
import type { PartStatus, RepairPart, RepairPriority, RepairStatus } from '@/api/types';
import { Badge, type Tone } from '@/components/ui/Badge';
import { Button, IconButton } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

export const REPAIR_STATUS_TONE: Record<RepairStatus, Tone> = {
  open: 'warn',
  in_progress: 'info',
  done: 'ok',
  cancelled: 'neutral',
};

export const PRIORITY_TONE: Record<RepairPriority, Tone> = { urgent: 'danger', normal: 'neutral', low: 'neutral' };

export const PART_TONE: Record<PartStatus, Tone> = { to_buy: 'warn', ordered: 'info', bought: 'ok' };

const errorText = (error: unknown) => (error instanceof Error ? error.message : undefined);

/** „12,50" и „12.50" са едно и също — майсторът пише както му дойде. */
export function parseDecimal(raw: string): number | null {
  const text = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!text) return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

export function money(amount: number | null, ccy: string): string {
  if (amount === null) return '—';
  return new Intl.NumberFormat('bg-BG', { style: 'currency', currency: ccy, maximumFractionDigits: 2 }).format(amount);
}

/**
 * Един ремонт отблизо: състояние, спешност, описание и частите — какво да се
 * купи, по колко, на каква цена. Цените въвежда майсторът.
 */
export function RepairPanel({ repairId, onClose, canEdit }: { repairId: string | null; onClose: () => void; canEdit: boolean }) {
  const { data: repair, isLoading } = useRepair(repairId ?? undefined);
  const update = useUpdateRepair();
  const addPart = useAddPart();
  const [notes, setNotes] = useState('');
  const [part, setPart] = useState({ name: '', quantity: '1', price: '', supplier: '' });

  useEffect(() => {
    setNotes(repair?.notes ?? '');
  }, [repair?.id, repair?.notes]);

  if (!repairId) return null;

  const setStatus = (status: RepairStatus) =>
    repair &&
    update.mutate(
      { id: repair.id, status },
      {
        onSuccess: () => toast.ok(`Ремонтът е „${REPAIR_STATUS_LABELS[status]}"`),
        onError: (error) => toast.error('Не стана', errorText(error)),
      },
    );

  const submitPart = () => {
    if (!repair) return;
    const quantity = parseDecimal(part.quantity) ?? 1;
    const price = parseDecimal(part.price);
    addPart.mutate(
      {
        repairId: repair.id,
        name: part.name.trim(),
        quantity: quantity > 0 ? quantity : 1,
        unitPrice: price,
        ...(part.supplier.trim() ? { supplier: part.supplier.trim() } : {}),
      },
      {
        onSuccess: () => setPart({ name: '', quantity: '1', price: '', supplier: '' }),
        onError: (error) => toast.error('Частта не е добавена', errorText(error)),
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      width="xl"
      title={repair ? repair.title : 'Ремонт'}
      description={repair ? `${repair.vehicleLabel} · заведен ${formatDateTime(repair.createdAt)}${repair.createdByName ? ` от ${repair.createdByName}` : ''}` : undefined}
      footer={
        <Button variant="ghost" onClick={onClose}>
          Затвори
        </Button>
      }
    >
      {isLoading || !repair ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={REPAIR_STATUS_TONE[repair.status]}>{REPAIR_STATUS_LABELS[repair.status]}</Badge>
            <Badge tone={PRIORITY_TONE[repair.priority]}>{REPAIR_PRIORITY_LABELS[repair.priority]}</Badge>
            {repair.totals.map((t) => (
              <span key={t.ccy} className="font-mono text-sm tabular">
                {money(t.amount, t.ccy)}
              </span>
            ))}
            {repair.partsWithoutPrice > 0 ? (
              <span className="text-xs text-warn">{repair.partsWithoutPrice} без цена</span>
            ) : null}
            {canEdit ? (
              <span className="ml-auto flex flex-wrap gap-1.5">
                {repair.status === 'open' ? (
                  <Button size="sm" variant="secondary" loading={update.isPending} onClick={() => setStatus('in_progress')}>
                    Започни
                  </Button>
                ) : null}
                {repair.status === 'open' || repair.status === 'in_progress' ? (
                  <>
                    <Button size="sm" variant="primary" loading={update.isPending} onClick={() => setStatus('done')}>
                      Готово
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setStatus('cancelled')}>
                      Откажи
                    </Button>
                  </>
                ) : (
                  <Button size="sm" variant="secondary" onClick={() => setStatus('open')}>
                    Отвори пак
                  </Button>
                )}
              </span>
            ) : null}
          </div>

          {repair.description ? <p className="whitespace-pre-line text-sm text-ink-dim">{repair.description}</p> : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Спешност">
              <Select
                value={repair.priority}
                disabled={!canEdit}
                onChange={(e) => update.mutate({ id: repair.id, priority: e.target.value as RepairPriority })}
                options={(['urgent', 'normal', 'low'] as RepairPriority[]).map((p) => ({ value: p, label: REPAIR_PRIORITY_LABELS[p] }))}
              />
            </Field>
            <Field label="Бележки на майстора">
              <Textarea
                rows={2}
                value={notes}
                disabled={!canEdit}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== (repair.notes ?? '') && update.mutate({ id: repair.id, notes: notes.trim() || null })}
              />
            </Field>
          </div>

          <div>
            <div className="mb-1.5 text-xs font-medium uppercase tracking-wide text-ink-faint">Части — какво да се купи</div>
            {repair.parts.length === 0 ? (
              <p className="text-xs text-ink-faint">Още няма части.</p>
            ) : (
              <ul className="divide-y divide-line rounded-lg border border-line">
                {repair.parts.map((p) => (
                  <PartRow key={p.id} part={p} canEdit={canEdit} />
                ))}
              </ul>
            )}
            {canEdit ? (
              <div className="mt-2 grid gap-2 rounded-lg border border-dashed border-line-strong p-2 sm:grid-cols-[1fr_5rem_7rem_10rem_auto]">
                <Input placeholder="Част, например Накладки предни" value={part.name} onChange={(e) => setPart({ ...part, name: e.target.value })} />
                <Input placeholder="Брой" inputMode="decimal" value={part.quantity} onChange={(e) => setPart({ ...part, quantity: e.target.value })} />
                <Input placeholder="Цена € / бр." inputMode="decimal" value={part.price} onChange={(e) => setPart({ ...part, price: e.target.value })} />
                <Input placeholder="Доставчик" value={part.supplier} onChange={(e) => setPart({ ...part, supplier: e.target.value })} />
                <Button variant="primary" loading={addPart.isPending} disabled={part.name.trim().length < 2} onClick={submitPart}>
                  Добави
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </Dialog>
  );
}

/** Една част: колко, цена, доставчик, купена ли е. Цената се допълва по-късно. */
function PartRow({ part, canEdit }: { part: RepairPart; canEdit: boolean }) {
  const update = useUpdatePart();
  const remove = useDeletePart();
  const [price, setPrice] = useState(part.unitPrice === null ? '' : String(part.unitPrice).replace('.', ','));
  useEffect(() => setPrice(part.unitPrice === null ? '' : String(part.unitPrice).replace('.', ',')), [part.unitPrice]);

  const savePrice = () => {
    const value = parseDecimal(price);
    if (value === part.unitPrice) return;
    update.mutate({ id: part.id, unitPrice: value }, { onError: (error) => toast.error('Цената не е записана', errorText(error)) });
  };

  return (
    <li className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
      <span className="min-w-0 flex-1">
        <span className="font-medium">{part.name}</span>
        <span className="text-ink-dim"> × {part.quantity.toLocaleString('bg-BG')}</span>
        {part.supplier ? <span className="text-xs text-ink-faint"> · {part.supplier}</span> : null}
      </span>
      {canEdit ? (
        <Input
          className="w-24 text-right"
          inputMode="decimal"
          placeholder="цена"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          onBlur={savePrice}
          onKeyDown={(e) => e.key === 'Enter' && savePrice()}
          aria-label="Единична цена"
        />
      ) : (
        <span className="font-mono tabular">{money(part.unitPrice, part.ccy)}</span>
      )}
      <span className="w-24 text-right font-mono tabular">{money(part.total, part.ccy)}</span>
      {canEdit ? (
        <Select
          className="w-36"
          value={part.status}
          onChange={(e) => update.mutate({ id: part.id, status: e.target.value as PartStatus })}
          options={(['to_buy', 'ordered', 'bought'] as PartStatus[]).map((s) => ({ value: s, label: PART_STATUS_LABELS[s] }))}
        />
      ) : (
        <Badge tone={PART_TONE[part.status]}>{PART_STATUS_LABELS[part.status]}</Badge>
      )}
      {canEdit ? (
        <IconButton
          label="Махни частта"
          onClick={() => window.confirm(`Да махна ли „${part.name}"?`) && remove.mutate(part.id)}
        >
          <Trash2 className="size-4" />
        </IconButton>
      ) : null}
    </li>
  );
}

